import * as PIXI from 'pixi.js';
import {
    assertInEnum,
    assertMinimum,
    assertNumber,
    assertIsValidUrl,
    assertNumberBetween,
    assertString,
    assertBoolean,
    assertIsValidAlpha,
    assertIsValidHexColor
} from '../../validators';
import { Config } from '../../video-data/config';


const defaultStyle = {
    align: 'left',
    dropShadow: false,
    dropShadowAlpha: 1,
    dropShadowAngle: Math.PI / 6,
    dropShadowBlur: 10,
    dropShadowColor: 0x000000,
    dropShadowDistance: 5,
    fill: 0x000000,
    fontSize: 26,
    fontUrl: Config.DEFAULT_FONT_URL,
    leading: 0,
    maxTextHeight: Infinity,
    readingDirection: Config.READING_DIRECTION.LTR,
    stroke: 0x000000, // rendering supported in WordElement, but not configurable in TextAsset
    strokeThickness: 0, // rendering supported in WordElement, but not configurable in TextAsset
};

/**
 * Utility function to ensure that object properties are copied by value, and not by reference
 *
 * @param {Object} target Target object to copy properties into
 * @param {Object} source Source object for the properties to copy
 * @param {Object} propertyObj Object containing properties names we want to loop over
 */
function deepCopyProperties(target, source, propertyObj) {
    for (const prop in propertyObj) {
        if (Array.isArray(source[prop])) {
            target[prop] = source[prop].slice();
        }
        else {
            target[prop] = source[prop];
        }
    }
}

/**
 * A WordStyle Object contains information to decorate TextAsset objects. This duplicates some
 * functionality from [PIXI.TextStyle]{@link https://pixijs.download/dev/docs/PIXI.TextStyle.html},
 * but not everything is supported. In addition some properties have been added such as fontUrl,
 * maxTextHeight and readingDirection.
 * The following are explicitly not supported, even though PIXI.TextStyle defines them:
 *  breakWords,
 *  fillGradientType,
 *  fillGradientStops,
 *  fontFamily,
 *  fontStyle,
 *  fontVariant,
 *  fontWeight,
 *  letterSpacing, (we could support this, but it will break ligatures)
 *  lineHeight,
 *  lineJoin,
 *  miterLimit,
 *  padding,
 *  textBaseline,
 *  trim (don't need this because fontkit perfectly fits the bounding box already),
 *  whiteSpace
 *
 * @see [PIXI.TextStyle]{@link https://pixijs.download/dev/docs/PIXI.TextStyle.html}
 */
export class WordStyle { // TODO disconnected from PIXI.TextStyle, makes sense?
    /** @type {Luminary.VideoData.Config.TEXT_ALIGN_HORIZONTAL} */
    #align;
    #dropShadow: boolean;
    #dropShadowAlpha: number;
    #dropShadowAngle: number;
    #dropShadowBlur: number;
    #dropShadowColor: number;
    #dropShadowDistance: number;
    #fill: number;
    #fontSize: number;
    #fontUrl: string;
    #leading: number;
    #maxTextHeight: number;
    /** @type {Luminary.VideoData.Config.READING_DIRECTION} */
    #readingDirection;
    #stroke: number;
    #strokeThickness: number;

    /**
     * @param {object} [style] - The style parameters
     * @param {Luminary.VideoData.Config.TEXT_ALIGN_HORIZONTAL} [style.align='left'] - horizontal text alignment
     * @param {boolean} [style.dropShadow=false] - Set a drop shadow for the text
     * @param {number} [style.dropShadowAlpha=1] - Set alpha for the drop shadow
     * @param {number} [style.dropShadowAngle=Math.PI/6] - Set a angle of the drop shadow
     * @param {number} [style.dropShadowBlur=0] - Set a shadow blur radius
     * @param {number} [style.dropShadowColor=0x000000] - A fill style to be used on the drop shadow
     * @param {number} [style.dropShadowDistance=5] - Set a distance of the drop shadow
     * @param {number} [style.fill=0x000000] - The fill style to be used on the text
     * @param {number} [style.fontSize=26] - The font size (px)
     * @param {string} [style.fontUrl=Config.DEFAULT_FONT_URL] - Url to font file (.ttf or .otf)
     * @param {number} [style.leading=0] - Spacing between lines, default is 0
     * @param {number} [style.maxTextHeight=Infinity] - The maximum total height of the text,
     * will truncate the text if it can't all fit.
     * @param {Luminary.VideoData.Config.READING_DIRECTION} [style.readingDirection='ltr'] - the reading direction of the text
     * @param {number} [style.stroke=0x000000] - The color of the text stroke
     * @param {number} [style.strokeThickness=0] - A number that represents the thickness of the stroke.
     *  Default is 0 (no stroke)
     */
    constructor(style?) {
        this.reset();
        deepCopyProperties(this, style, style);
    }

    /**
     * Horizontal text alignment.
     *
     * @type {Luminary.VideoData.Config.TEXT_ALIGN_HORIZONTAL}
     */
    get align() {
        return this.#align;
    }

    set align(value) {
        assertString(value);
        assertInEnum(value, Config.TEXT_ALIGN_HORIZONTAL);
        if (this.#align !== value) {
            this.#align = value;
        }
    }

    /**
     * Enables a drop shadow for the text.
     */
    get dropShadow(): boolean {
        return this.#dropShadow;
    }

    set dropShadow(value: boolean) {
        assertBoolean(value);
        if (this.#dropShadow !== value) {
            this.#dropShadow = value;
        }
    }

    /**
     * Set alpha for the drop shadow.
     */
    get dropShadowAlpha(): number {
        return this.#dropShadowAlpha;
    }

    set dropShadowAlpha(value: number) {
        assertIsValidAlpha(value);
        if (this.#dropShadowAlpha !== value) {
            this.#dropShadowAlpha = value;
        }
    }

    /**
     * The angle (radians) of the drop shadow.
     */
    get dropShadowAngle(): number {
        return this.#dropShadowAngle;
    }

    set dropShadowAngle(value: number) {
        assertNumberBetween(value, -2 * Math.PI, 2 * Math.PI);
        if (this.#dropShadowAngle !== value) {
            this.#dropShadowAngle = value;
        }
    }

    /**
     * The blur radius of the drop shadow.
     */
    get dropShadowBlur(): number {
        return this.#dropShadowBlur;
    }

    set dropShadowBlur(value: number) {
        assertNumber(value);
        if (this.#dropShadowBlur !== value) {
            this.#dropShadowBlur = value;
        }
    }

    /**
     * The color of the drop shadow as a number.
     */
    get dropShadowColor(): number {
        return this.#dropShadowColor;
    }

    set dropShadowColor(value: number | string) {
        if (typeof value === 'string') {
            value = PIXI.utils.string2hex(value);
        }
        assertIsValidHexColor(value);
        if (this.#dropShadowColor !== value) {
            this.#dropShadowColor = value;
        }
    }

    /**
     * The distance of the drop shadow.
     */
    get dropShadowDistance(): number {
        return this.#dropShadowDistance;
    }

    set dropShadowDistance(value: number) {
        assertNumber(value);
        if (this.#dropShadowDistance !== value) {
            this.#dropShadowDistance = value;
        }
    }

    /**
     * The fill style to be used on the text.
     */
    get fill(): number {
        return this.#fill;
    }

    set fill(value: number | string) {
        if (typeof value === 'string') {
            value = PIXI.utils.string2hex(value);
        }
        assertIsValidHexColor(value);
        if (this.#fill !== value) {
            this.#fill = value;
        }
    }

    /**
     * The font size (px) of the text.
     */
    set fontSize(value: number) {
        assertMinimum(value, 0);
        if (this.#fontSize !== value) {
            this.#fontSize = value;
        }
    }

    get fontSize(): number {
        return this.#fontSize;
    }

    /**
     * Url to font file (.ttf or .otf).
     */
    get fontUrl(): string {
        return this.#fontUrl;
    }

    set fontUrl(value: string) {
        assertIsValidUrl(value);
        if (this.#fontUrl !== value) {
            this.#fontUrl = value;
        }
    }

    /**
     * Sets the leading (spacing between lines).
     */
    get leading(): number {
        return this.#leading;
    }

    set leading(value: number) {
        assertNumber(value);
        if (this.#leading !== value) {
            this.#leading = value;
        }
    }

    /**
     * The maximum overall height of the text. Will truncate text if it can't all fit.
     */
    get maxTextHeight(): number {
        return this.#maxTextHeight;
    }

    set maxTextHeight(value: number) {
        assertMinimum(value, 0);
        if (this.#maxTextHeight !== value) {
            this.#maxTextHeight = value;
        }
    }

    /**
     * The direction in which the text reads.
     *
     * @type {Luminary.VideoData.Config.READING_DIRECTION}
     */
    get readingDirection() {
        return this.#readingDirection;
    }

    set readingDirection(value) {
        assertInEnum(value, Config.READING_DIRECTION);
        if (this.#readingDirection !== value) {
            this.#readingDirection = value;
        }
    }

    /**
     * The color of the text stroke.
     */
    get stroke(): number {
        return this.#stroke;
    }

    set stroke(value: number) {
        if (typeof value === 'string') {
            value = PIXI.utils.string2hex(value);
        }
        assertIsValidHexColor(value);
        if (this.#stroke !== value) {
            this.#stroke = value;
        }
    }

    /**
     * The thickness of the stroke, zero means no stroke.
     */
    get strokeThickness(): number {
        return this.#strokeThickness;
    }

    set strokeThickness(value: number) {
        assertMinimum(value, 0);
        if (this.#strokeThickness !== value) {
            this.#strokeThickness = value;
        }
    }

    /**
     * Gets the distance of the drop shadow in the x-direction.
     */
    get dropShadowDistanceX(): number {
        if (!this.dropShadow) {
            return 0;
        }
        return Math.cos(this.dropShadowAngle) * this.dropShadowDistance;
    }

    /**
     * Gets the distance of the drop shadow in the y-direction.
     */
    get dropShadowDistanceY(): number {
        if (!this.dropShadow) {
            return 0;
        }
        return Math.sin(this.dropShadowAngle) * this.dropShadowDistance;
    }

    /**
     * Resets all properties to the defaults specified in defaultStyle
     */
    reset() {
        deepCopyProperties(this, defaultStyle, defaultStyle);
    }

    /**
     * Creates a deep copy of the WordStyle object.
     */
    clone(): WordStyle {
        const clonedProperties = {};
        deepCopyProperties(clonedProperties, this, defaultStyle);

        return new WordStyle(clonedProperties);
    }

    equals(other: WordStyle) {
        return this.align === other.align
            && this.dropShadow === other.dropShadow
            && this.dropShadowAlpha === other.dropShadowAlpha
            && this.dropShadowAngle === other.dropShadowAngle
            && this.dropShadowBlur === other.dropShadowBlur
            && this.dropShadowColor === other.dropShadowColor
            && this.dropShadowDistance === other.dropShadowDistance
            && this.fill === other.fill
            && this.fontSize === other.fontSize
            && this.fontUrl === other.fontUrl
            && this.leading === other.leading
            && this.maxTextHeight === other.maxTextHeight
            && this.readingDirection === other.readingDirection
            && this.stroke === other.stroke
            && this.strokeThickness === other.strokeThickness;
    }
}
